import {
  CheckOutlined,
  ClockCircleOutlined,
  CommentOutlined,
} from "@ant-design/icons"
import { Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { type ReactNode } from "react"

import Flex from "#blocks/Flex"
import FormattedTextBlock from "#blocks/FormattedTextBlock"

import { PortfolioMockStage } from "./usePortfolioDbStorage"

const { Text } = Typography

interface PortfolioStepStatusCellProps {
  status: PortfolioMockStage["status"]
  /** Due date for the step; rendered inside the tooltip when present. */
  dueDate?: string
  /** Optional feedback text; when present, it's shown inside the hover tooltip
   *  beneath the status label. */
  feedbackText?: string
  /** When false, callers wrap the icon in their own Tooltip and supply the
   *  body via `renderStepStatusTooltipContent`. Defaults to true. */
  withTooltip?: boolean
}

const statusLabelMap: Record<PortfolioMockStage["status"], string> = {
  not_started: "Not started",
  in_progress: "In progress",
  review: "In review",
  complete: "Complete",
  has_feedback: "Has feedback",
}

const getCellColors = (status: PortfolioMockStage["status"]) => {
  if (status === "complete") return { border: "#389e0d", bg: "#f6ffed" }
  if (status === "has_feedback") return { border: "#cf1322", bg: "#fff1f0" }
  if (status === "in_progress") return { border: "#1677ff", bg: "#e6f4ff" }
  if (status === "review") return { border: "#d48806", bg: "#fffbe6" }
  return { border: "#d9d9d9", bg: "#ffffff" }
}

export const renderStepStatusTooltipContent = ({
  status,
  dueDate,
  feedbackText,
}: {
  status: PortfolioMockStage["status"]
  dueDate?: string
  feedbackText?: string
}): ReactNode => {
  const hasFeedback = !!feedbackText?.trim()
  const dueLabel =
    dueDate && dayjs(dueDate).isValid()
      ? `Due ${dayjs(dueDate).format("MMM D, YYYY")}`
      : "No due date"

  return (
    <div style={{ maxWidth: 280 }}>
      <div style={{ fontWeight: 500 }}>{statusLabelMap[status]}</div>
      <div style={{ fontSize: 12, marginTop: 2 }}>{dueLabel}</div>
      {hasFeedback ? (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontWeight: 500, fontSize: 12 }}>Feedback:</div>
          <FormattedTextBlock
            text={feedbackText!}
            style={{ padding: 0, marginTop: 2, fontSize: 12 }}
          />
        </div>
      ) : null}
    </div>
  )
}

const PortfolioStepStatusCell = ({
  status,
  dueDate,
  feedbackText,
  withTooltip = true,
}: PortfolioStepStatusCellProps) => {
  const colors = getCellColors(status)

  const iconBox = (
    <Flex
      center
      style={{
        width: 18,
        height: 18,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        borderRadius: 3,
      }}
    >
      {status === "complete" ? (
        <CheckOutlined style={{ color: "#389e0d", fontSize: 11 }} />
      ) : null}
      {status === "has_feedback" ? (
        <CommentOutlined style={{ color: "#cf1322", fontSize: 11 }} />
      ) : null}
      {status === "in_progress" ? (
        <ClockCircleOutlined style={{ color: "#1677ff", fontSize: 10 }} />
      ) : null}
      {status === "review" ? (
        <Text style={{ color: "#d48806", fontSize: 10, lineHeight: "10px" }}>
          !
        </Text>
      ) : null}
    </Flex>
  )

  if (!withTooltip) return iconBox

  return (
    <Tooltip
      title={renderStepStatusTooltipContent({ status, dueDate, feedbackText })}
      mouseEnterDelay={0.2}
    >
      {iconBox}
    </Tooltip>
  )
}

export default PortfolioStepStatusCell
